import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MultiPolygon, Polygon } from "geojson";
import uniq from "lodash/uniq";

import { useLazyGetLayerFeatureTotalsQuery } from "fond/api";
import { canFetchSublayerTotals, FeatureTotals } from "fond/map/featureTotalsSummary";
import { LayerConfig } from "fond/types/ProjectLayerConfig";

interface UseSublayerFeatureTotalsProps {
  versionId?: string;
  selectionGeometry?: Polygon | MultiPolygon | null;
  /**
   * Layers whose totals have to be broken down per sublayer, loaded as soon as they appear.
   */
  partialLayerConfigs: LayerConfig[];
}

interface UseSublayerFeatureTotalsReturns {
  /** Sublayer totals, keyed by sublayer configuration ID. */
  totals: FeatureTotals;
  loadLayerFeatureTotals: (layerConfig: LayerConfig) => void;
  isFetching: boolean;
  /** Whether any of `partialLayerConfigs` is still awaiting its breakdown. */
  isLoadingSublayerTotals: boolean;
}

/**
 * Totals loaded for the sublayers of a layer, tagged with the selection they were loaded for so that
 * totals belonging to a superseded selection are never reported.
 */
type LoadedTotals = { geometry: Polygon | MultiPolygon | null; totals: FeatureTotals; layerConfigIds: string[] };

const EMPTY_LOADED_TOTALS: LoadedTotals = { geometry: null, totals: {}, layerConfigIds: [] };

/**
 * Load the per-sublayer breakdown of a layer's feature totals.
 */
const useSublayerFeatureTotals = ({
  versionId,
  selectionGeometry,
  partialLayerConfigs,
}: UseSublayerFeatureTotalsProps): UseSublayerFeatureTotalsReturns => {
  const [loadFeatureTotals, { isFetching }] = useLazyGetLayerFeatureTotalsQuery();
  const [loadedTotals, setLoadedTotals] = useState<LoadedTotals>(EMPTY_LOADED_TOTALS);

  const selection = selectionGeometry ?? null;

  // The selection current when a response arrives, which need not be the one its request captured.
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  // Totals loaded for a previous selection are stale, so they don't count as loaded.
  const loaded = loadedTotals.geometry === selection ? loadedTotals : EMPTY_LOADED_TOTALS;

  const loadLayerFeatureTotals = useCallback(
    (layerConfig: LayerConfig) => {
      if (!layerConfig?.ID || !canFetchSublayerTotals(layerConfig)) return;

      loadFeatureTotals(selection ? { versionId, layerId: layerConfig.Key, geometry: selection } : { versionId, layerId: layerConfig.Key }).then(
        (response) => {
          // The selection moved on while we were waiting, so this response is of no use to anyone.
          if (selectionRef.current !== selection) return;

          setLoadedTotals((prev) => {
            const base = prev.geometry === selection ? prev : EMPTY_LOADED_TOTALS;
            return {
              geometry: selection,
              totals: { ...base.totals, ...response.data },
              layerConfigIds: uniq([...base.layerConfigIds, layerConfig.ID]),
            };
          });
        }
      );
    },
    [loadFeatureTotals, versionId, selection]
  );

  useEffect(() => {
    // Skip layers already loaded for the current selection
    partialLayerConfigs.filter(({ ID }) => !loaded.layerConfigIds.includes(ID)).forEach(loadLayerFeatureTotals);
  }, [partialLayerConfigs, loadLayerFeatureTotals, loaded.layerConfigIds]);

  const isLoadingSublayerTotals = partialLayerConfigs.some(({ ID }) => !loaded.layerConfigIds.includes(ID));

  return useMemo(
    () => ({ totals: loaded.totals, loadLayerFeatureTotals, isFetching, isLoadingSublayerTotals }),
    [loaded.totals, loadLayerFeatureTotals, isFetching, isLoadingSublayerTotals]
  );
};

export default useSublayerFeatureTotals;
